import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "./ui"
import {
  adoptMeasurementResources,
  createMeasurementGtmVersion,
  discoverMeasurementResources,
  importLiveMeasurementDocumentation,
  listMeasurementDocumentation,
  preflightMeasurementDocumentation,
  publishMeasurementGtmVersion,
  reviewMeasurementDocumentation,
  saveDomainMeasurementMapping,
  saveMeasurementDraft,
  startMeasurementOAuth,
} from "../api/measurementDocumentation"
import {
  exportMeasurementCsv,
  exportMeasurementPdf,
} from "../utils/measurementDocumentationExport"
import { navigateCurrentWindow } from "../utils/browserNavigation"
import "./DomainMeasurementDocumentation.css"

const emptyData = { mappings: [], google_authorizations: [], websites: [] }

const normalizeDomain = (value) => {
  const raw = String(value || "").trim()
  if (!raw) return ""
  try {
    const parsed = new URL(/^[a-z][a-z\d+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`)
    return parsed.hostname.toLowerCase().replace(/^www\./, "")
  } catch {
    return ""
  }
}

const revisionBadge = (status) => ({
  published: "accent",
  version_created: "outline",
  reviewed: "outline",
  draft: "subtle",
  superseded: "subtle",
}[status] || "subtle")

const resourceRefs = (item) => [
  item?.resource_id ? { resource_type: "tag", resource_id: item.resource_id } : null,
  ...(item?.triggers || []).map((resource) => ({ resource_type: "trigger", resource_id: resource.resource_id })),
  ...(item?.variables || []).map((resource) => ({ resource_type: "variable", resource_id: resource.resource_id })),
].filter(Boolean)

const JsonField = ({ label, value, onChange, disabled }) => {
  const serialized = useMemo(() => JSON.stringify(value || {}, null, 2), [value])
  const [text, setText] = useState(serialized)
  const [error, setError] = useState("")
  useEffect(() => setText(serialized), [serialized])
  const commit = () => {
    try {
      const parsed = JSON.parse(text || "{}")
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error()
      onChange(parsed)
      setError("")
    } catch {
      setError("Enter a valid JSON object.")
    }
  }
  return (
    <label className="ui-field measurement-docs__json-field">
      <span className="ui-label">{label}</span>
      <textarea
        className="ui-textarea"
        rows={4}
        value={text}
        disabled={disabled}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
      />
      {error && <span className="ui-error">{error}</span>}
    </label>
  )
}

export default function DomainMeasurementDocumentation({
  organizationId,
  account,
  canManage,
  selectedMappingId,
  onSelectMapping,
}) {
  const [data, setData] = useState(emptyData)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState("")
  const [message, setMessage] = useState(null)
  const [showMappingForm, setShowMappingForm] = useState(false)
  const [resources, setResources] = useState({ containers: [], properties: [] })
  const [form, setForm] = useState({
    domain: "", website_url_id: "", google_oauth_connection_id: "",
    gtm: "", ga4: "",
  })
  const [draftSpec, setDraftSpec] = useState(null)
  const [selectedAdoptions, setSelectedAdoptions] = useState(new Set())
  const [preflight, setPreflight] = useState(null)

  const load = useCallback(async () => {
    if (!organizationId || !account?.id) return
    setLoading(true)
    try {
      setData(await listMeasurementDocumentation(organizationId, account.id))
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setLoading(false)
    }
  }, [account?.id, organizationId])

  useEffect(() => { load() }, [load])

  const selectedMapping = useMemo(() =>
    data.mappings.find((item) => item.id === selectedMappingId) || null,
  [data.mappings, selectedMappingId])
  const activeRevision = selectedMapping?.revisions?.find((item) => item.status === "draft") ||
    selectedMapping?.revisions?.find((item) => item.status === "version_created") ||
    selectedMapping?.revisions?.find((item) => item.status === "reviewed") ||
    selectedMapping?.revisions?.find((item) => item.status === "published") ||
    selectedMapping?.revisions?.[0] || null
  const activeRevisionSpec = activeRevision?.spec
  const exportRevision = selectedMapping?.revisions?.find((item) => item.status === "published") ||
    selectedMapping?.revisions?.find((item) => ["reviewed", "version_created"].includes(item.status)) ||
    activeRevision
  const adoptions = useMemo(() => new Set((selectedMapping?.adoptions || []).map((item) =>
    `${item.resource_type}:${item.external_resource_id}`
  )), [selectedMapping?.adoptions])
  const latestVersionRun = selectedMapping?.sync_runs?.find((item) =>
    item.status === "version_created" && item.revision_id === activeRevision?.id
  ) || null

  useEffect(() => {
    setDraftSpec(activeRevisionSpec ? JSON.parse(JSON.stringify(activeRevisionSpec)) : null)
    setSelectedAdoptions(new Set())
    setPreflight(null)
  }, [activeRevision?.id, activeRevisionSpec])

  const perform = async (key, action, success) => {
    setBusy(key)
    setMessage(null)
    try {
      const result = await action()
      if (success) setMessage({ type: "success", text: success })
      await load()
      return result
    } catch (error) {
      setMessage({ type: "error", text: error.message, code: error.code })
      return null
    } finally {
      setBusy("")
    }
  }

  const startOAuth = async (purpose = "read", connectionId = null) => {
    setBusy(`oauth-${purpose}`)
    try {
      const result = await startMeasurementOAuth(organizationId, account.id, purpose, connectionId)
      sessionStorage.setItem("measurement-documentation-oauth-pending", "true")
      navigateCurrentWindow(result.authorization_url)
    } catch (error) {
      setMessage({ type: "error", text: error.message })
      setBusy("")
    }
  }

  const discover = async () => {
    if (!form.google_oauth_connection_id) return
    setBusy("discover")
    try {
      const result = await discoverMeasurementResources(organizationId, form.google_oauth_connection_id)
      setResources(result)
      setForm((current) => ({
        ...current,
        gtm: result.containers?.[0]
          ? `${result.containers[0].account_id}:${result.containers[0].container_id}`
          : "",
      }))
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const selectedDomain = normalizeDomain(form.domain)
  const streamOptions = useMemo(() => resources.properties.flatMap((property) =>
    (property.streams || []).map((stream) => ({ property, stream }))
  ), [resources.properties])
  useEffect(() => {
    if (!selectedDomain || !streamOptions.length || form.ga4) return
    const match = streamOptions.find(({ stream }) => normalizeDomain(stream.default_uri) === selectedDomain)
    if (match) setForm((current) => ({
      ...current,
      ga4: `${match.property.property_id}:${match.stream.stream_id}`,
    }))
  }, [form.ga4, selectedDomain, streamOptions])

  const saveMapping = async (event) => {
    event.preventDefault()
    const [gtmAccountId, gtmContainerId] = form.gtm.split(":")
    const separator = form.ga4.indexOf(":")
    const ga4PropertyId = form.ga4.slice(0, separator)
    const ga4StreamId = form.ga4.slice(separator + 1)
    if (!selectedDomain || !gtmContainerId || !ga4StreamId) return
    const result = await perform("save-mapping", () => saveDomainMeasurementMapping(organizationId, {
      account_id: account.id,
      domain: selectedDomain,
      website_url_id: form.website_url_id || null,
      google_oauth_connection_id: form.google_oauth_connection_id,
      gtm_account_id: gtmAccountId,
      gtm_container_id: gtmContainerId,
      ga4_property_id: ga4PropertyId,
      ga4_stream_id: ga4StreamId,
    }), "Domain measurement mapping saved.")
    if (result?.mapping) {
      setShowMappingForm(false)
      onSelectMapping(result.mapping.id)
    }
  }

  const updateCollectionItem = (collection, index, patch) => setDraftSpec((current) => ({
    ...current,
    [collection]: current[collection].map((item, itemIndex) =>
      itemIndex === index ? { ...item, ...patch } : item
    ),
  }))

  const toggleAdoption = (item) => {
    const refs = resourceRefs(item).filter((resource) => !adoptions.has(`${resource.resource_type}:${resource.resource_id}`))
    setSelectedAdoptions((current) => {
      const next = new Set(current)
      const shouldAdd = refs.some((resource) => !next.has(`${resource.resource_type}:${resource.resource_id}`))
      refs.forEach((resource) => {
        const key = `${resource.resource_type}:${resource.resource_id}`
        if (shouldAdd) next.add(key)
        else next.delete(key)
      })
      return next
    })
  }

  const addEvent = () => setDraftSpec((current) => ({
    ...current,
    events: [...(current.events || []), {
      resource_type: "tag",
      resource_id: "",
      managed_key: `new:${Date.now()}`,
      name: "New GA4 event",
      tag_type: "gaawe",
      event_name: "",
      reporting_label: "",
      purpose: "",
      notes: "",
      parameter_mappings: {},
      triggers: [],
      variables: [],
      supported: true,
    }],
  }))

  const adoptSelected = () => perform("adopt", async () => {
    await saveMeasurementDraft(organizationId, activeRevision.id, draftSpec)
    return adoptMeasurementResources(
      organizationId,
      selectedMapping.id,
      activeRevision.id,
      [...selectedAdoptions].map((key) => {
        const [resource_type, ...id] = key.split(":")
        return { resource_type, resource_id: id.join(":") }
      }),
    )
  }, "Selected GTM resources are now explicitly managed.")

  const runPreflight = async () => {
    const result = await perform("preflight", () => preflightMeasurementDocumentation(
      organizationId, activeRevision.id,
    ))
    if (result) setPreflight(result)
  }

  if (loading && !data.mappings.length) return <Card><CardContent><p>Loading domain documentation…</p></CardContent></Card>

  return (
    <Card className="measurement-docs">
      <CardHeader>
        <div className="measurement-docs__header">
          <div>
            <CardTitle>Domain measurement mappings</CardTitle>
            <CardDescription>
              Connect each domain to its GTM container and GA4 web stream, then maintain a reporting-ready implementation record.
            </CardDescription>
          </div>
          {canManage && <Button onClick={() => setShowMappingForm((value) => !value)}>
            {showMappingForm ? "Cancel" : "Add domain mapping"}
          </Button>}
        </div>
      </CardHeader>
      <CardContent className="ui-stack ui-stack--lg">
        {message && <p className={message.type === "error" ? "ui-error" : "ui-hint"} role={message.type === "error" ? "alert" : "status"}>{message.text}</p>}

        {showMappingForm && canManage && (
          <form className="ui-stack ui-stack--md measurement-docs__mapping-form" onSubmit={saveMapping}>
            {!data.google_authorizations.length ? (
              <div className="ui-button-row">
                <Button onClick={() => startOAuth("read")} disabled={Boolean(busy)}>
                  {busy === "oauth-read" ? "Opening Google…" : "Connect Google for GTM and GA4"}
                </Button>
              </div>
            ) : (
              <div className="ui-grid ui-grid--form">
                <Input
                  label="Domain"
                  placeholder="example.com"
                  value={form.domain}
                  onChange={(event) => setForm({ ...form, domain: event.target.value, website_url_id: "", ga4: "" })}
                  required
                />
                <label className="ui-field">
                  <span className="ui-label">Existing account website (optional)</span>
                  <select className="ui-input" value={form.website_url_id} onChange={(event) => {
                    const website = data.websites.find((item) => item.id === event.target.value)
                    setForm({ ...form, website_url_id: event.target.value, domain: website?.url || form.domain, ga4: "" })
                  }}>
                    <option value="">Add or match by domain</option>
                    {data.websites.map((website) => <option key={website.id} value={website.id}>{website.url}</option>)}
                  </select>
                </label>
                <label className="ui-field">
                  <span className="ui-label">Google authorization</span>
                  <select className="ui-input" value={form.google_oauth_connection_id} onChange={(event) => setForm({ ...form, google_oauth_connection_id: event.target.value, gtm: "", ga4: "" })} required>
                    <option value="">Select Google account</option>
                    {data.google_authorizations.map((connection) => <option key={connection.id} value={connection.id}>{connection.google_email || connection.id}</option>)}
                  </select>
                </label>
                <div className="ui-field measurement-docs__discover-action"><span className="ui-label">Accessible resources</span><Button variant="outline" onClick={discover} disabled={!form.google_oauth_connection_id || busy === "discover"}>{busy === "discover" ? "Discovering…" : "Discover GTM and GA4"}</Button></div>
                {!!resources.containers.length && <label className="ui-field"><span className="ui-label">GTM web container</span><select className="ui-input" value={form.gtm} onChange={(event) => setForm({ ...form, gtm: event.target.value })} required><option value="">Select container</option>{resources.containers.map((container) => <option key={`${container.account_id}:${container.container_id}`} value={`${container.account_id}:${container.container_id}`}>{container.container_name} ({container.public_id})</option>)}</select></label>}
                {!!streamOptions.length && <label className="ui-field"><span className="ui-label">GA4 web stream</span><select className="ui-input" value={form.ga4} onChange={(event) => setForm({ ...form, ga4: event.target.value })} required><option value="">Select web stream</option>{streamOptions.map(({ property, stream }) => <option key={`${property.property_id}:${stream.stream_id}`} value={`${property.property_id}:${stream.stream_id}`}>{property.property_name} / {stream.stream_name} ({stream.measurement_id}){normalizeDomain(stream.default_uri) === selectedDomain ? " — domain match" : ""}</option>)}</select></label>}
              </div>
            )}
            {!!resources.containers.length && <div className="ui-button-row"><Button type="submit" disabled={busy === "save-mapping" || !form.gtm || !form.ga4}>{busy === "save-mapping" ? "Saving…" : "Save mapping"}</Button></div>}
          </form>
        )}

        {!data.mappings.length ? <p className="ui-text--muted">No domain mappings have been configured for this account.</p> : (
          <TableContainer>
            <Table>
              <TableHead><TableRow><TableHeaderCell>Domain</TableHeaderCell><TableHeaderCell>GTM</TableHeaderCell><TableHeaderCell>GA4 stream</TableHeaderCell><TableHeaderCell>Documentation</TableHeaderCell><TableHeaderCell align="right">Action</TableHeaderCell></TableRow></TableHead>
              <TableBody>{data.mappings.map((mapping) => {
                const current = mapping.revisions?.find((revision) => revision.status === "published") || mapping.revisions?.[0]
                return <TableRow key={mapping.id}><TableCell><strong>{mapping.normalized_domain}</strong></TableCell><TableCell>{mapping.gtm_container_name}<span className="measurement-docs__secondary">{mapping.gtm_public_id}</span></TableCell><TableCell>{mapping.ga4_stream_name}<span className="measurement-docs__secondary">{mapping.ga4_measurement_id}</span></TableCell><TableCell>{current ? <Badge variant={revisionBadge(current.status)}>v{current.version} · {current.status}</Badge> : <Badge variant="subtle">Not imported</Badge>}</TableCell><TableCell align="right"><Button size="sm" variant="outline" onClick={() => onSelectMapping(mapping.id)}>{mapping.id === selectedMappingId ? "Close" : "Open documentation"}</Button></TableCell></TableRow>
              })}</TableBody>
            </Table>
          </TableContainer>
        )}

        {selectedMapping && (
          <section className="measurement-docs__editor ui-stack ui-stack--lg">
            <div className="measurement-docs__editor-header">
              <div><h3>{selectedMapping.normalized_domain}</h3><p className="ui-text--muted">{selectedMapping.gtm_public_id} → {selectedMapping.ga4_measurement_id}</p></div>
              <div className="ui-button-row ui-button-row--compact">
                {exportRevision && <><Button size="sm" variant="outline" onClick={() => exportMeasurementPdf(selectedMapping, exportRevision, account.name)}>Export PDF</Button><Button size="sm" variant="outline" onClick={() => exportMeasurementCsv(selectedMapping, exportRevision)}>Export CSV</Button></>}
                {canManage && <Button size="sm" onClick={() => perform("import", () => importLiveMeasurementDocumentation(organizationId, selectedMapping.id), "Live GTM configuration imported as a draft.")} disabled={busy === "import"}>{busy === "import" ? "Importing…" : "Import live GTM"}</Button>}
              </div>
            </div>

            {!activeRevision ? <p>Import the live GTM container to create the first documentation draft.</p> : draftSpec && <>
              <div className="ui-chip-row"><Badge variant={revisionBadge(activeRevision.status)}>Revision {activeRevision.version} · {activeRevision.status}</Badge>{activeRevision.source_live_version && <Badge variant="subtle">Source {activeRevision.source_live_version.split("/").pop()}</Badge>}</div>
              <div className="ui-grid ui-grid--form">
                <label className="ui-field"><span className="ui-label">Implementation summary</span><textarea className="ui-textarea" rows={4} disabled={!canManage || activeRevision.status !== "draft"} value={draftSpec.summary || ""} onChange={(event) => setDraftSpec({ ...draftSpec, summary: event.target.value })} /></label>
                <label className="ui-field"><span className="ui-label">Client and reporting notes</span><textarea className="ui-textarea" rows={4} disabled={!canManage || activeRevision.status !== "draft"} value={draftSpec.client_notes || ""} onChange={(event) => setDraftSpec({ ...draftSpec, client_notes: event.target.value })} /></label>
              </div>

              <h4>GA4 configuration</h4>
              {(draftSpec.configurations || []).map((item, index) => <div className="measurement-docs__resource" key={`config:${item.resource_id}`}>
                <div className="measurement-docs__resource-title"><strong>{item.name}</strong><Badge variant={adoptions.has(`tag:${item.resource_id}`) ? "accent" : "subtle"}>{adoptions.has(`tag:${item.resource_id}`) ? "Adopted" : "Documented only"}</Badge></div>
                <div className="ui-grid ui-grid--form"><Input label="Reporting label" disabled={!canManage || activeRevision.status !== "draft"} value={item.reporting_label || ""} onChange={(event) => updateCollectionItem("configurations", index, { reporting_label: event.target.value })} /><Input label="Measurement ID" disabled value={selectedMapping.ga4_measurement_id} /><Input label="Purpose" disabled={!canManage || activeRevision.status !== "draft"} value={item.purpose || ""} onChange={(event) => updateCollectionItem("configurations", index, { purpose: event.target.value })} /><Input label="Notes" disabled={!canManage || activeRevision.status !== "draft"} value={item.notes || ""} onChange={(event) => updateCollectionItem("configurations", index, { notes: event.target.value })} /></div>
                {canManage && activeRevision.status === "draft" && !adoptions.has(`tag:${item.resource_id}`) && <label className="measurement-docs__adopt"><input type="checkbox" checked={selectedAdoptions.has(`tag:${item.resource_id}`)} onChange={() => toggleAdoption(item)} /> Adopt this tag and its dependencies for managed GTM updates</label>}
              </div>)}

              <div className="measurement-docs__section-title"><h4>GA4 events</h4>{canManage && activeRevision.status === "draft" && <Button size="sm" variant="outline" onClick={addEvent}>Add event</Button>}</div>
              {(draftSpec.events || []).map((item, index) => {
                const adopted = !item.resource_id || adoptions.has(`tag:${item.resource_id}`)
                const editableTechnical = canManage && activeRevision.status === "draft" && adopted
                return <div className="measurement-docs__resource" key={item.resource_id || item.managed_key}>
                  <div className="measurement-docs__resource-title"><strong>{item.name || item.event_name || "New GA4 event"}</strong><Badge variant={adopted ? "accent" : "subtle"}>{item.resource_id ? adopted ? "Adopted" : "Documented only" : "New managed event"}</Badge></div>
                  <div className="ui-grid ui-grid--form"><Input label="Reporting label (required)" required disabled={!canManage || activeRevision.status !== "draft"} value={item.reporting_label || ""} onChange={(event) => updateCollectionItem("events", index, { reporting_label: event.target.value })} /><Input label="GA4 event name" disabled={!editableTechnical} value={item.event_name || ""} onChange={(event) => updateCollectionItem("events", index, { event_name: event.target.value })} /><Input label="Business purpose (required)" required disabled={!canManage || activeRevision.status !== "draft"} value={item.purpose || ""} onChange={(event) => updateCollectionItem("events", index, { purpose: event.target.value })} /><Input label="Notes" disabled={!canManage || activeRevision.status !== "draft"} value={item.notes || ""} onChange={(event) => updateCollectionItem("events", index, { notes: event.target.value })} /></div>
                  <p className="measurement-docs__secondary">Trigger: {(item.triggers || []).map((trigger) => `${trigger.name} (${trigger.type})`).join(", ") || "A domain-scoped custom event trigger will be created."}</p>
                  <JsonField label="GA4 parameter mappings" disabled={!editableTechnical} value={item.parameter_mappings} onChange={(parameter_mappings) => updateCollectionItem("events", index, { parameter_mappings })} />
                  {item.triggers?.[0] && adopted && <JsonField label="Primary trigger configuration" disabled={!editableTechnical || !adoptions.has(`trigger:${item.triggers[0].resource_id}`)} value={item.triggers[0].source_payload} onChange={(source_payload) => updateCollectionItem("events", index, { triggers: item.triggers.map((trigger, triggerIndex) => triggerIndex === 0 ? { ...trigger, source_payload } : trigger) })} />}
                  {canManage && activeRevision.status === "draft" && item.resource_id && !adopted && <label className="measurement-docs__adopt"><input type="checkbox" checked={selectedAdoptions.has(`tag:${item.resource_id}`)} onChange={() => toggleAdoption(item)} /> Adopt this event tag and its dependencies for managed GTM updates</label>}
                  {editableTechnical && item.resource_id && <label className="measurement-docs__remove"><input type="checkbox" checked={item.remove_from_gtm === true} onChange={(event) => updateCollectionItem("events", index, { remove_from_gtm: event.target.checked })} /> Explicitly remove this tag from GTM in the next version</label>}
                </div>
              })}

              {!!draftSpec.unsupported?.length && <details><summary>{draftSpec.unsupported.length} read-only GTM resources are documented but out of sync scope</summary><TableContainer><Table><TableHead><TableRow><TableHeaderCell>Resource</TableHeaderCell><TableHeaderCell>Type</TableHeaderCell><TableHeaderCell>Reason</TableHeaderCell></TableRow></TableHead><TableBody>{draftSpec.unsupported.map((item) => <TableRow key={item.resource_id}><TableCell>{item.name}</TableCell><TableCell>{item.tag_type}</TableCell><TableCell>{item.reason}</TableCell></TableRow>)}</TableBody></Table></TableContainer></details>}

              {canManage && activeRevision.status === "draft" && <div className="ui-button-row">
                <Button onClick={() => perform("save-draft", () => saveMeasurementDraft(organizationId, activeRevision.id, draftSpec), "Documentation draft saved.")} disabled={busy === "save-draft"}>{busy === "save-draft" ? "Saving…" : "Save draft"}</Button>
                {!!selectedAdoptions.size && <Button variant="outline" onClick={adoptSelected} disabled={busy === "adopt"}>{busy === "adopt" ? "Adopting…" : `Adopt ${selectedAdoptions.size} selected resources`}</Button>}
                <Button variant="outline" onClick={() => perform("review", async () => { await saveMeasurementDraft(organizationId, activeRevision.id, draftSpec); return reviewMeasurementDocumentation(organizationId, activeRevision.id) }, "Documentation reviewed and frozen for GTM preflight.")} disabled={Boolean(busy)}>Review documentation</Button>
              </div>}

              {canManage && ["reviewed", "version_created"].includes(activeRevision.status) && <div className="ui-stack ui-stack--md measurement-docs__sync">
                <div className="ui-button-row"><Button variant="outline" onClick={runPreflight} disabled={busy === "preflight"}>{busy === "preflight" ? "Checking…" : "Review GTM changes"}</Button>{activeRevision.status === "reviewed" && <Button onClick={() => perform("create-version", () => createMeasurementGtmVersion(organizationId, activeRevision.id), "Validated GTM version created. Review it before publishing.")} disabled={Boolean(busy)}>Create GTM version</Button>}{latestVersionRun && <Button onClick={() => perform("publish", () => publishMeasurementGtmVersion(organizationId, latestVersionRun.id), "GTM version published and documentation marked current.")} disabled={Boolean(busy)}>Publish GTM version</Button>}</div>
                {message?.code === "gtm_edit_scope_required" && <Button variant="outline" onClick={() => startOAuth("edit", selectedMapping.google_oauth_connection_id)}>Authorize GTM editing</Button>}
                {message?.code === "gtm_publish_scope_required" && <Button variant="outline" onClick={() => startOAuth("publish", selectedMapping.google_oauth_connection_id)}>Authorize GTM publishing</Button>}
                {preflight && <div className="measurement-docs__diff"><h4>Preflight diff</h4><div className="ui-chip-row"><Badge variant="accent">{preflight.diff.creates.length} creates</Badge><Badge variant="outline">{preflight.diff.updates.length} updates</Badge><Badge variant="outline">{preflight.diff.removes.length} removals</Badge><Badge variant={preflight.diff.conflicts.length ? "outline" : "subtle"}>{preflight.diff.conflicts.length} conflicts</Badge><Badge variant="subtle">{preflight.diff.untouched.length} untouched</Badge></div>{preflight.diff.conflicts.map((item) => <p className="ui-error" key={item.key}>{item.name}: {item.reason}</p>)}</div>}
              </div>}
            </>}
          </section>
        )}
      </CardContent>
    </Card>
  )
}
